import { isValidationError } from '@/backend/errors/isValidationError';
import {
  printTopLevelValidationError,
  printValidationError,
} from '@/backend/errors/printValidationError';

export type InvalidResult = ValidationError | ValidationException;

export class ValidationError {
  name: string;
  message: string;
  stack: string;

  constructor(
    problemCode: string,
    description: string,
    path: string[],
    actual: string,
    expected: string,
  );
  constructor(
    problemCode: string,
    description: string,
    path: string[],
    inner: InvalidResult[],
  );
  constructor(
    problemCode: string,
    description: string,
    public readonly path: ReadonlyArray<string>,
    ...args: [string, string] | [InvalidResult[]]
  ) {
    let details: { expected: string; actual: string } | InvalidResult[];
    let inner: InvalidResult[] | null = null;
    let comparison: { expected: string; actual: string } | null = null;
    if (args.length === 1) {
      inner = details = args[0];
    } else {
      const [actual, expected] = args;
      comparison = details = {
        actual,
        expected,
      };
    }

    this.name = 'ValidationError';
    this.description = description;
    this.problemCode = problemCode;
    this.inner = inner;
    this.details = details;
    this.comparison = comparison;

    this.message = printTopLevelValidationError(this);
    this.stack = this.message;
  }

  typeName?: string;
  description: string;
  problemCode: string;
  inner: InvalidResult[] | null;
  details: { expected: string; actual: string } | InvalidResult[];
  comparison: { expected: string; actual: string } | null;

  toException() {
    if (this.comparison) {
      return new ValidationException(
        this.problemCode,
        this.description,
        [...this.path],
        this.comparison.actual,
        this.comparison.expected,
      );
    }

    return new ValidationException(
      this.problemCode,
      this.description,
      [...this.path],
      this.inner!,
    );
  }

  assert() {
    throw this.toException();
  }

  format(): string {
    return printValidationError(this) + '\n' + this.stack;
  }

  fingerprint() {
    return [this.path, this.problemCode, this.comparison?.actual];
  }

  static from(description: string): ValidationError {
    return new ValidationError('ValidationError', description, [], '', '');
  }

  static assertValid<T>(input: T | InvalidResult): asserts input is T {
    if (isValidationError(input)) {
      input.assert();
    }
  }
}

export class ValidationException extends Error {
  constructor(
    problemCode: string,
    description: string,
    path: string[],
    actual: string,
    expected: string,
  );
  constructor(
    problemCode: string,
    description: string,
    path: string[],
    inner: InvalidResult[],
  );
  constructor(
    problemCode: string,
    description: string,
    public readonly path: ReadonlyArray<string>,
    ...args: [string, string] | [InvalidResult[]]
  ) {
    let details: { expected: string; actual: string } | InvalidResult[];
    let inner: InvalidResult[] | null = null;
    let comparison: { expected: string; actual: string } | null = null;
    if (args.length === 1) {
      inner = details = args[0];
    } else {
      const [actual, expected] = args;
      comparison = details = {
        actual,
        expected,
      };
    }

    super(description, { cause: inner ? inner[0] : null });
    // super(message, inner, 'ValidationError', 400);

    this.name = 'ValidationError';
    this.description = description;
    this.problemCode = problemCode;
    this.inner = inner;
    this.details = details;
    this.comparison = comparison;

    this.message = printTopLevelValidationError(this);
  }

  // @ts-expect-error not sure why typescript thinks there won't be a stack
  stack: string;
  typeName?: string;
  description: string;
  problemCode: string;
  inner: InvalidResult[] | null;
  details: { expected: string; actual: string } | InvalidResult[];
  comparison: { expected: string; actual: string } | null;

  format(): string {
    return printValidationError(this) + '\n' + this.stack;
  }

  fingerprint() {
    return [this.path, this.problemCode, this.comparison?.actual];
  }

  toException() {
    return this;
  }

  assert() {
    throw this.toException();
  }

  static from(description: string): ValidationException {
    return new ValidationException('ValidationError', description, [], '', '');
  }
}
